import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useDispatch } from 'react-redux';

import { NOTIFICATION_DEVICE_ID_KEY, NOTIFICATION_FCM_TOKEN_KEY } from '../../constants/notifications';
import { obtainFcmToken, subscribeForegroundPush } from '../../services/firebaseWebPush';
import { connectNotificationSocket, type NotificationWsPayload } from '../../services/notificationWebSocket';
import { api } from '../../store/services/api';
import { getAccessToken } from '../../utils/authToken';
import { useRegisterNotificationDeviceMutation } from '../../store/services/notificationsApi';
import { useAppSelector } from '../../hooks/redux';

let fcmDeviceRegisterInFlight: Promise<void> | null = null;

const toastOnceByNotificationId = new Map<number, number>();
const TOAST_BY_ID_TTL_MS = 120_000;

function pruneToastDedupeMap(now: number) {
  toastOnceByNotificationId.forEach((t, id) => {
    if (now - t > TOAST_BY_ID_TTL_MS) toastOnceByNotificationId.delete(id);
  });
}

function shouldSuppressDuplicateToast(notificationId: number | undefined): boolean {
  if (notificationId == null || !Number.isFinite(notificationId)) return false;
  const now = Date.now();
  pruneToastDedupeMap(now);
  if (toastOnceByNotificationId.has(notificationId)) return true;
  toastOnceByNotificationId.set(notificationId, now);
  return false;
}

function extractNotificationIdFromWsPayload(
  payload: NotificationWsPayload | Record<string, unknown>
): number | undefined {
  const p = payload as NotificationWsPayload & { id?: unknown };
  if (typeof p.id === 'number' && Number.isFinite(p.id)) return p.id;
  if (typeof p.id === 'string') {
    const n = Number(String(p.id).trim());
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function parseToastFromPayload(payload: NotificationWsPayload | Record<string, unknown>): {
  title: string;
  body: string;
} {
  const p = payload as NotificationWsPayload & { message?: string };
  const title = p.title ?? p.notification?.title ?? 'Notification';
  const rawBody = p.body ?? p.message ?? p.notification?.body;
  const body = rawBody === null || rawBody === undefined ? '' : typeof rawBody === 'string' ? rawBody : String(rawBody);
  return { title: String(title), body };
}

const RealtimeNotifications: FC = () => {
  const dispatch = useDispatch();
  const accessToken = useAppSelector(state => state.auth.userInfo?.access) ?? getAccessToken();
  const [registerDevice] = useRegisterNotificationDeviceMutation();
  const [snackbar, setSnackbar] = useState<{
    key: number;
    open: boolean;
    title: string;
    body: string;
  }>({
    key: 0,
    open: false,
    title: '',
    body: '',
  });

  const showToast = useCallback((title: string, body: string) => {
    setSnackbar(s => ({
      key: s.key + 1,
      open: true,
      title,
      body,
    }));
  }, []);

  const handleIncoming = useCallback(
    (payload: NotificationWsPayload | Record<string, unknown>) => {
      const notificationId = extractNotificationIdFromWsPayload(payload);
      const { title, body } = parseToastFromPayload(payload);
      if (!shouldSuppressDuplicateToast(notificationId)) {
        showToast(title, body);
      }
      dispatch(api.util.invalidateTags([{ type: 'Notification', id: 'LIST' }]));
    },
    [dispatch, showToast]
  );

  const handleIncomingRef = useRef(handleIncoming);
  handleIncomingRef.current = handleIncoming;

  useEffect(() => {
    if (!accessToken) return;

    const closeWs = connectNotificationSocket(payload => handleIncomingRef.current(payload));
    return () => {
      closeWs();
    };
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    let unsubscribeFcm: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
          await Notification.requestPermission();
        }
      } catch {
        /* ignore */
      }
      if (cancelled) return;

      const fcmToken = await obtainFcmToken();
      if (cancelled) return;
      if (!fcmToken) {
        console.warn(
          '[FCM] No FCM token — backend device registration skipped; localStorage will not have',
          NOTIFICATION_DEVICE_ID_KEY,
          '(set Firebase/VAPID env, HTTPS, grant notification permission). Web push is limited on iOS Safari unless the site is added to Home Screen.'
        );
      } else {
        // Always re-register on session so backend stays in sync after tab close/reload
        // (localStorage alone can be stale if the device was deleted server-side).
        if (fcmDeviceRegisterInFlight) {
          await fcmDeviceRegisterInFlight;
          if (cancelled) return;
        } else {
          fcmDeviceRegisterInFlight = (async () => {
            try {
              const device = await registerDevice({ fcm_token: fcmToken, platform: 'web' }).unwrap();
              if (device?.id != null && Number.isFinite(Number(device.id))) {
                localStorage.setItem(NOTIFICATION_DEVICE_ID_KEY, String(device.id));
                localStorage.setItem(NOTIFICATION_FCM_TOKEN_KEY, fcmToken);
              } else {
                console.error('[FCM] POST /notifications/devices/ succeeded but response had no numeric id:', device);
              }
            } catch (err) {
              console.error('[FCM] POST /notifications/devices/ failed — push targeting may not work:', err);
            } finally {
              fcmDeviceRegisterInFlight = null;
            }
          })();
          await fcmDeviceRegisterInFlight;
          if (cancelled) return;
        }
      }

      if (cancelled) return;
      unsubscribeFcm = subscribeForegroundPush(({ title, body, notificationId }) => {
        if (!shouldSuppressDuplicateToast(notificationId)) {
          showToast(title ?? 'Notification', body ?? '');
        }
        dispatch(api.util.invalidateTags([{ type: 'Notification', id: 'LIST' }]));
      });
    })();

    return () => {
      cancelled = true;
      unsubscribeFcm?.();
    };
  }, [accessToken, dispatch, registerDevice, showToast]);

  return (
    <Snackbar
      key={snackbar.key}
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: theme => Math.max(theme.zIndex.snackbar, 14000) }}
    >
      <Alert
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        severity="success"
        variant="filled"
        sx={{ width: '100%' }}
      >
        <strong>{snackbar.title}</strong>
        {snackbar.body ? (
          <>
            <br />
            {snackbar.body}
          </>
        ) : null}
      </Alert>
    </Snackbar>
  );
};

export default RealtimeNotifications;
